// The catalog admin API (docs/12, row S45): task types, workflows, their draft, publishing and
// versions. Keys start with "catalog" so they never clash with the legacy workflow queries.
import { queryOptions } from "@tanstack/react-query";

import { api } from "../api/client";
import { requestJson } from "../api/http";
import { ApiError, unwrap } from "../api/queries";
import { parseWorkflow, parseWorkflows } from "./parse";
import type { Issue, TaskType, TaskTypeInput, TemplateStatus, VersionDocument, VersionDto, WorkflowInput, WorkflowSummary } from "./types";

const taskTypePath = (id: string) => ({ params: { path: { task_type_id: id } } });
const workflowPath = (id: string) => ({ params: { path: { workflow_id: id } } });
const enc = encodeURIComponent;

/** Every task type, or those matching `q` (name, slug, description), best first. */
export const taskTypesQuery = (q = "") =>
  queryOptions({
    queryKey: ["catalog", "task-types", q],
    queryFn: () => unwrap(api.GET("/api/admin/task-types", { params: { query: q ? { q } : {} } })),
  });

export const createTaskType = (body: TaskTypeInput): Promise<TaskType> => unwrap(api.POST("/api/admin/task-types", { body }));

export const updateTaskType = (id: string, body: TaskTypeInput): Promise<TaskType> =>
  unwrap(api.PUT("/api/admin/task-types/{task_type_id}", { ...taskTypePath(id), body }));

export async function deleteTaskType(id: string): Promise<void> {
  const { response, error } = await api.DELETE("/api/admin/task-types/{task_type_id}", taskTypePath(id));
  if (!response.ok) throw new ApiError(response.status, error);
}

export interface WorkflowFilter {
  status?: TemplateStatus;
  taskType?: string;
  /** Search on name, slug and description; the API ranks the matches. */
  q?: string;
}

export const workflowsQuery = (filter: WorkflowFilter = {}) =>
  queryOptions({
    queryKey: ["catalog", "workflows", "list", filter],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (filter.status) q.set("status", filter.status);
      if (filter.taskType) q.set("task_type", filter.taskType);
      if (filter.q) q.set("q", filter.q);
      const query = q.size > 0 ? `?${q}` : "";
      return parseWorkflows(await requestJson("GET", `/api/admin/workflows${query}`));
    },
  });

export const workflowQuery = (id: string) =>
  queryOptions({
    queryKey: ["catalog", "workflows", id],
    queryFn: async () => parseWorkflow(await requestJson("GET", `/api/admin/workflows/${enc(id)}`)),
  });

export const createWorkflow = async (body: WorkflowInput): Promise<WorkflowSummary> =>
  parseWorkflow(await requestJson("POST", "/api/admin/workflows", body));

export const updateWorkflow = async (id: string, body: WorkflowInput): Promise<WorkflowSummary> =>
  parseWorkflow(await requestJson("PUT", `/api/admin/workflows/${enc(id)}`, body));

export const setWorkflowStatus = async (id: string, status: TemplateStatus): Promise<WorkflowSummary> =>
  parseWorkflow(await requestJson("PUT", `/api/admin/workflows/${enc(id)}/status`, { status }));

export async function deleteWorkflow(id: string): Promise<void> {
  await requestJson("DELETE", `/api/admin/workflows/${enc(id)}`);
}

export interface Draft {
  draft: VersionDto;
  issues: Issue[];
}

/** The draft with its findings, or null when the workflow has none yet. */
export const draftQuery = (id: string) =>
  queryOptions({
    queryKey: ["catalog", "workflows", id, "draft"],
    queryFn: async (): Promise<Draft | null> => {
      try {
        return await unwrap(api.GET("/api/admin/workflows/{workflow_id}/draft", workflowPath(id)));
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    staleTime: Infinity,
  });

export const saveDraft = (id: string, doc: VersionDocument, changelog: string): Promise<Draft> =>
  unwrap(api.PUT("/api/admin/workflows/{workflow_id}/draft", { ...workflowPath(id), body: { ...doc, changelog } }));

/** Throws an ApiError with status 422 and `issues` in its body when the draft is invalid. */
export const publishDraft = (id: string, changelog: string): Promise<VersionDto> =>
  unwrap(api.POST("/api/admin/workflows/{workflow_id}/publish", { ...workflowPath(id), body: { changelog } }));

/** The broker actions a pre-execution script may be granted (D77); fixed for a server build. */
export const scriptActionsQuery = () =>
  queryOptions({
    queryKey: ["catalog", "script-actions"],
    queryFn: () => unwrap(api.GET("/api/admin/script-actions")),
    staleTime: Infinity,
  });

export const versionsQuery = (id: string) =>
  queryOptions({
    queryKey: ["catalog", "workflows", id, "versions"],
    queryFn: () => unwrap(api.GET("/api/admin/workflows/{workflow_id}/versions", workflowPath(id))),
  });

export const versionQuery = (id: string, version: number) =>
  queryOptions({
    queryKey: ["catalog", "workflows", id, "versions", version],
    queryFn: () =>
      unwrap(api.GET("/api/admin/workflows/{workflow_id}/versions/{version}", { params: { path: { workflow_id: id, version } } })),
    staleTime: Infinity,
  });
